'use client';

import { useQuery } from 'convex-helpers/react/cache/hooks';
import { api } from 'convex/_generated/api';
import { useUpsertPlantSowingConfig } from '@api/plantSowingConfig';
import { useGardenConfigStore } from '@shared/store/useGardenConfigStore';
import type {
  Garden,
  Plant,
  SowingConfigField,
  SowingConfigFieldValue,
  SowingConfigValues,
} from '@modules/garden-config/models';
import {
  createHandleBulkApplySowingConfig,
  createHandleUpdateSowingConfig,
  getPlantsInGarden,
  getSowingConfigForPlant,
} from '@modules/garden-config/business_logic';
import { ComponentType } from 'react';

export interface SowingConfigProps {
  plantConfigs: Array<{ plant: Plant; config: SowingConfigValues }>;
  selectedPlantIds: Set<string>;
  bulkSeedBuffer: number;
  bulkIsActive: boolean;
  onUpdateConfig: (
    plantId: string,
    field: SowingConfigField,
    value: SowingConfigFieldValue
  ) => void;
  onToggleSelection: (plantId: string) => void;
  onSelectAll: (plantIds: string[]) => void;
  onBulkApply: (seedBuffer: number, isActive: boolean) => void;
  onClearSelection: () => void;
  onBulkSeedBufferChange: (buffer: number) => void;
  onBulkIsActiveChange: (active: boolean) => void;
}

interface SowingConfigContainerProps {
  View: ComponentType<SowingConfigProps>;
  selectedGarden: Garden;
  plants: Plant[];
}

export function SowingConfigContainer({
  View,
  selectedGarden,
  plants,
}: SowingConfigContainerProps) {
  const selectedPlantIds = useGardenConfigStore((s) => s.selectedPlantIds);
  const bulkSeedBuffer = useGardenConfigStore((s) => s.bulkSeedBuffer);
  const bulkIsActive = useGardenConfigStore((s) => s.bulkIsActive);
  const togglePlantSelection = useGardenConfigStore((s) => s.togglePlantSelection);
  const selectAllPlants = useGardenConfigStore((s) => s.selectAllPlants);
  const clearSelection = useGardenConfigStore((s) => s.clearSelection);
  const setBulkSeedBuffer = useGardenConfigStore((s) => s.setBulkSeedBuffer);
  const setBulkIsActive = useGardenConfigStore((s) => s.setBulkIsActive);

  const updatePlantSowingConfig = useUpsertPlantSowingConfig();

  const configs = useQuery(api.plantSowingConfig.queries.getSowingConfigsByGarden, {
    gardenId: selectedGarden._id,
  });
  const plantDefaults = useQuery(api.userPlantDefaults.queries.getUserPlantDefaults, {});
  const preferences = useQuery(api.userPreferences.queries.getUserPreferences, {});

  const plantsInGarden = getPlantsInGarden(selectedGarden, plants);
  const storeConfigs = configs ?? [];
  const storeDefaults = plantDefaults ?? [];

  const plantConfigs = plantsInGarden.map((plant) => ({
    plant,
    config: getSowingConfigForPlant(
      storeConfigs,
      selectedGarden._id,
      plant.id,
      plant.category,
      storeDefaults,
      plant,
      preferences
    ),
  }));

  const handleUpdateConfig = createHandleUpdateSowingConfig(
    selectedGarden,
    storeConfigs,
    updatePlantSowingConfig,
    storeDefaults,
    plants,
    preferences
  );

  const handleBulkApply = createHandleBulkApplySowingConfig(
    selectedGarden,
    selectedPlantIds,
    storeConfigs,
    updatePlantSowingConfig,
    clearSelection,
    storeDefaults,
    plants,
    preferences
  );

  if (configs === undefined || plantDefaults === undefined || preferences === undefined) {
    return null;
  }

  return (
    <View
      plantConfigs={plantConfigs}
      selectedPlantIds={selectedPlantIds}
      bulkSeedBuffer={bulkSeedBuffer}
      bulkIsActive={bulkIsActive}
      onUpdateConfig={handleUpdateConfig}
      onToggleSelection={togglePlantSelection}
      onSelectAll={selectAllPlants}
      onBulkApply={handleBulkApply}
      onClearSelection={clearSelection}
      onBulkSeedBufferChange={setBulkSeedBuffer}
      onBulkIsActiveChange={setBulkIsActive}
    />
  );
}
